import React, { useEffect, useState, useCallback } from 'react';
import { TrendingUp, Eye, MessageSquare, Target, RefreshCw, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) + ' ' +
      d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div
    data-testid={testid}
    className="bg-white/[0.03] border border-white/10 rounded-lg p-4 flex items-start gap-3"
  >
    <div className="w-9 h-9 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-[#D4AF37]" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1">{label}</div>
      <div className="text-2xl font-bold text-white tabular-nums leading-none">{value}</div>
      {sub && <div className="text-[11px] text-white/50 mt-1.5">{sub}</div>}
    </div>
  </div>
);

export const DealAnalyticsTab = ({ getStoredPassword }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const pwd = encodeURIComponent(getStoredPassword?.() || '');
      const r = await fetch(`${API_URL}/api/admin/deals-analytics?password=${pwd}&days=${days}`);
      if (!r.ok) {
        throw new Error(`HTTP ${r.status}`);
      }
      setData(await r.json());
    } catch (e) {
      setErr(e.message || 'Failed to load deal analytics');
    } finally {
      setLoading(false);
    }
  }, [days, getStoredPassword]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || { deals: 0, visible: 0, views: 0, unique_views: 0, enquiries: 0, conversion_pct: 0 };
  const deals = data?.deals || [];

  return (
    <div className="space-y-5" data-testid="deal-analytics-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1">Per-Deal Funnel</div>
          <h2 className="text-xl font-semibold text-white">Deal Analytics</h2>
          <p className="text-[12px] text-white/50 mt-1">
            Views & enquiries per opportunity — 30-min view dedupe per visitor (IP + UA).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="deal-analytics-window"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="bg-black/40 border border-white/10 text-white text-[12px] rounded-md px-2.5 py-1.5 focus:outline-none focus:border-[#D4AF37]/60"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </select>
          <button
            data-testid="deal-analytics-refresh"
            onClick={load}
            className="px-3 py-1.5 rounded-md border border-white/10 hover:border-[#D4AF37]/60 text-white text-[12px] flex items-center gap-1.5 transition"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {err && (
        <div data-testid="deal-analytics-error" className="px-3 py-2 rounded-md border border-red-500/40 bg-red-500/10 text-red-300 text-[12px]">
          {err}
        </div>
      )}

      {/* Totals strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="deal-analytics-totals">
        <StatCard icon={Target} label="Deals tracked" value={totals.deals} sub={`${totals.visible} live`} testid="totals-deals" />
        <StatCard icon={Eye} label="Total views" value={totals.views} sub={`${totals.unique_views} unique`} testid="totals-views" />
        <StatCard icon={MessageSquare} label="Enquiries" value={totals.enquiries} testid="totals-enquiries" />
        <StatCard icon={TrendingUp} label="Conversion" value={`${totals.conversion_pct}%`} sub="enquiries / views" testid="totals-conversion" />
        <StatCard icon={Star} label="Window" value={`${data?.window_days || days}d`} sub={fmtDate(data?.generated_at)} testid="totals-window" />
      </div>

      {/* Per-deal table */}
      <div className="bg-white/[0.03] border border-white/10 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/50">Per-deal performance</div>
          <div className="text-[10px] text-white/40">sorted by enquiries → views</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="deal-analytics-table">
            <thead className="text-white/40 text-[10px] uppercase tracking-[0.12em]">
              <tr className="border-b border-white/5">
                <th className="text-left py-2.5 px-4 font-normal">Deal</th>
                <th className="text-right py-2.5 px-3 font-normal">Views</th>
                <th className="text-right py-2.5 px-3 font-normal">Unique</th>
                <th className="text-right py-2.5 px-3 font-normal">Enquiries</th>
                <th className="text-right py-2.5 px-3 font-normal">Conv %</th>
                <th className="text-left py-2.5 px-3 font-normal hidden md:table-cell">Last view</th>
                <th className="text-left py-2.5 px-3 font-normal hidden md:table-cell">Last enquiry</th>
              </tr>
            </thead>
            <tbody>
              {loading && deals.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-white/40 text-[12px]">Loading…</td></tr>
              )}
              {!loading && deals.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-white/40 text-[12px]">No deals yet.</td></tr>
              )}
              {deals.map((d) => {
                const isTop = d.enquiries > 0 && d.enquiries === Math.max(...deals.map(x => x.enquiries));
                return (
                  <tr key={d.id || d.slug} data-testid={`deal-analytics-row-${d.slug}`} className="border-b border-white/5 hover:bg-white/[0.02] transition">
                    <td className="py-3 px-4 max-w-md">
                      <div className="flex items-center gap-2">
                        {isTop && <Star className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" />}
                        <div className="min-w-0">
                          <div className="text-white truncate">{d.title || d.slug}</div>
                          <div className="text-white/40 text-[10px] truncate">
                            {[d.location, d.property_type].filter(Boolean).join(' · ') || '—'}
                            {!d.visible && <span className="ml-2 text-amber-400/70">(hidden)</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="text-right py-3 px-3 text-white tabular-nums">{d.views}</td>
                    <td className="text-right py-3 px-3 text-white/70 tabular-nums">{d.unique_views}</td>
                    <td className="text-right py-3 px-3 text-[#D4AF37] tabular-nums font-medium">{d.enquiries}</td>
                    <td className="text-right py-3 px-3 text-white/70 tabular-nums">{d.conversion_pct}%</td>
                    <td className="py-3 px-3 text-white/50 text-[11px] hidden md:table-cell">{fmtDate(d.last_view_at)}</td>
                    <td className="py-3 px-3 text-white/50 text-[11px] hidden md:table-cell">{fmtDate(d.last_enquiry_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DealAnalyticsTab;
